import config from '../config';

/**
 * 로고 URL 생성 헬퍼 함수
 *
 * 통합 로고(unified_logo_url)를 우선 사용하고,
 * 없으면 개별 로고(user_*_logo_url)를 사용합니다.
 *
 * [2026-05-19] 정적 폴백 경로(/img/nm_logo.svg) 제거.
 *   기본 로고는 admin-api uploads/logos/default-nm-logo.svg 로 시드되어
 *   DB(design_templates.unified_logo_url) 에서 정본을 제공한다.
 *   본 함수는 더 이상 코드 하드코딩 경로를 반환하지 않고,
 *   DB 값이 없는 극단 케이스에는 null 을 반환한다.
 *   호출 컴포넌트는 src={null} 일 때 <img> 를 렌더하지 않거나
 *   조건부 분기로 빈 영역을 처리해야 한다.
 *
 * @param {Object} designTemplate - 디자인 템플릿 데이터
 * @param {string} type - 로고 타입 ('main', 'footer', 'login', 'register')
 * @returns {string|null} 로고 이미지 URL (DB 정본) 또는 null
 *
 * @example
 * const logoUrl = getLogoUrl(designTemplate, 'login');
 * // Returns: '/uploads/logos/default-nm-logo.svg' 또는 운영자 업로드 경로
 */
export const getLogoUrl = (designTemplate, type = 'main') => {
  if (!designTemplate) {
    // designTemplate 가 아직 로드되지 않은 초기 렌더 상황.
    // 시드 이후에는 사실상 도달하지 않으나 안전망으로 null 반환.
    return null;
  }

  // 상대 경로를 절대 경로로 변환하는 헬퍼
  const getAbsoluteUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;

    // HTTPS 환경에서는 상대 경로 그대로 사용 (Nginx 프록시)
    return url;
  };

  // 1. 통합 로고 우선 (unified_logo_url)
  if (designTemplate.unified_logo_url) {
    return getAbsoluteUrl(designTemplate.unified_logo_url);
  }

  // 2. 개별 로고 폴백
  const logoUrlMap = {
    main: designTemplate.user_main_logo_url,
    footer: designTemplate.user_footer_logo_url,
    login: designTemplate.user_login_logo_url,
    register: designTemplate.user_login_logo_url // 회원가입도 로그인 로고 사용
  };

  const logoUrl = logoUrlMap[type];
  if (logoUrl) {
    return getAbsoluteUrl(logoUrl);
  }

  // 3. 마지막 안전망 — DB 컬럼이 모두 비었을 때.
  //    시드 이후에는 도달하지 않으나, DB 손상·운영자 삭제 등 극단 케이스 대비.
  //    하드코딩 경로 대신 null 을 반환해 호출 측에서 명시적으로 처리하도록 위임.
  return null;
};

/**
 * 로고 사이즈 반환 헬퍼 함수
 *
 * logo_sizes JSON에서 위치별 사이즈를 가져오거나,
 * 개별 필드(user_*_logo_width/height)를 사용합니다.
 *
 * @param {Object} designTemplate - 디자인 템플릿 데이터
 * @param {string} type - 로고 타입 ('main', 'footer', 'login', 'register')
 * @returns {Object} { width, height } 로고 크기 객체
 *
 * @example
 * const logoSize = getLogoSize(designTemplate, 'login');
 * // Returns: { width: 200, height: 'auto' }
 */
export const getLogoSize = (designTemplate, type = 'main') => {
  if (!designTemplate) {
    return { width: 200, height: 60 };
  }

  // 1. 통합 로고 사이즈 (logo_sizes JSON)
  if (designTemplate.unified_logo_url && designTemplate.logo_sizes) {
    const logoSizes = typeof designTemplate.logo_sizes === 'string'
      ? JSON.parse(designTemplate.logo_sizes)
      : designTemplate.logo_sizes;

    if (logoSizes[type]) {
      return logoSizes[type];
    }
  }

  // 2. 개별 로고 사이즈
  const sizeMap = {
    main: {
      width: designTemplate.user_main_logo_width || 180,
      height: designTemplate.user_main_logo_height || 50
    },
    footer: {
      width: designTemplate.user_footer_logo_width || 150,
      height: designTemplate.user_footer_logo_height || 40
    },
    login: {
      width: designTemplate.user_login_logo_width || 200,
      height: designTemplate.user_login_logo_height || 60
    },
    register: {
      width: designTemplate.user_login_logo_width || 200,
      height: designTemplate.user_login_logo_height || 60
    }
  };

  return sizeMap[type] || { width: 200, height: 60 };
};

/**
 * 로고 스타일 객체 생성 헬퍼 함수
 *
 * getLogoSize()의 결과를 CSS 스타일 객체로 변환합니다.
 *
 * @param {Object} designTemplate - 디자인 템플릿 데이터
 * @param {string} type - 로고 타입
 * @returns {Object} CSS 스타일 객체
 *
 * @example
 * <img src={logoUrl} style={getLogoStyle(designTemplate, 'login')} />
 */
export const getLogoStyle = (designTemplate, type = 'main') => {
  const size = getLogoSize(designTemplate, type);

  return {
    maxWidth: size.width === 'auto' ? 'none' : typeof size.width === 'number' ? `${size.width}px` : size.width,
    maxHeight: size.height === 'auto' ? 'none' : typeof size.height === 'number' ? `${size.height}px` : size.height
  };
};
